// src/store/builders/useProjectEnqStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "../useAuthStore";

const formatDateForAPI = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  if (dateString.length === 10 && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(dateString + "T00:00:00.000Z").toISOString();
  }
  return date.toISOString();
};

const useProjectEnqStore = create(
  persist(
    (set, get) => ({
      enquiries: [],
      isLoading: false,
      error: null,
      currentFilter: null,
      modalComments: [],
      isCommentsLoading: false,
      isAddingComment: false,

      // 🟢 Fetch Enquiries
      fetchEnquiries: async (status = null, limit = 10, page = 1) => {
        set({ isLoading: true, error: null, currentFilter: status });
        try {
          const { token, user } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          if (!user || !user.id) throw new Error("Builder ID missing!");
          const params = new URLSearchParams({ builderId: user.id, limit, page });
          if (status) params.append("status", status);
          const response = await axiosInstancev1.get(
            `/projects/enquiries?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          set({ enquiries: response.data.enquiries || [] });
        } catch (error) {
          console.error("Fetch Enquiries Error:", error);
          const errorMessage =
            error.response?.data?.message || "Failed to fetch enquiries";
          set({ error: errorMessage });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      },

      // 🟢 Fetch Enquiry Comments
      fetchEnquiryComments: async (enquiryId) => {
        set({ isCommentsLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          const response = await axiosInstancev1.get(
            `/projects/enquiry/${enquiryId}/comments`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            set({ modalComments: response.data.comments || [] });
          } else {
            throw new Error(
              response.data.message || "Failed to fetch comments"
            );
          }
        } catch (error) {
          console.error("Fetch Enquiry Comments Error:", error);
          const errorMessage =
            error.response?.data?.message || "Failed to fetch comments";
          set({ error: errorMessage, modalComments: [] });
          toast.error(errorMessage);
        } finally {
          set({ isCommentsLoading: false });
        }
      },

      // 🟢 Add Comment to Enquiry
      addEnquiryComment: async (enquiryId, commentText) => {
        set({ isAddingComment: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          if (!enquiryId) throw new Error("Enquiry ID is required!");
          if (!commentText.trim())
            throw new Error("Comment text cannot be empty.");

          const postCommentResponse = await axiosInstancev1.post(
            `/projects/enquiry/${enquiryId}/comments`,
            { comment: commentText },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (!postCommentResponse.data.success) {
            throw new Error(
              postCommentResponse.data.message ||
              "Failed to add comment to history."
            );
          }

          const updateEnquiryResponse = await axiosInstancev1.put(
            `/projects/enquiry/${enquiryId}`,
            { comments: commentText },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!updateEnquiryResponse.data.success) {
            console.warn(
              "Comment added, but main enquiry update failed:",
              updateEnquiryResponse.data.message
            );
            toast.warn(
              "Comment added, but main enquiry update had an issue."
            );
          } else {
            toast.success("Comment added and enquiry updated successfully!");
          }

          await get().fetchEnquiryComments(enquiryId);
          await get().fetchEnquiries(get().currentFilter);
          return true;
        } catch (error) {
          console.error("Add Enquiry Comment Error:", error);
          const errorMessage =
            error.response?.data?.message || "Failed to add comment.";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return false;
        } finally {
          set({ isAddingComment: false });
        }
      },

      // 🟢 Update Primary Comment
      updatePrimaryEnquiryComment: async (enquiryId, newComment) => {
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          if (!enquiryId) throw new Error("Enquiry ID is required!");
          const payload = { comments: newComment };
          await axiosInstancev1.put(`/projects/enquiry/${enquiryId}`, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          toast.success("Enquiry comment updated successfully!");
          await get().fetchEnquiries(get().currentFilter);
          return true;
        } catch (error) {
          console.error("Update Primary Comment Error:", error);
          const errorMessage =
            error.response?.data?.message || "Failed to update comment";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // 🟢 Send Project Invite (Supports any customer)
      sendProjectInvite: async (customerDucktailId, projectData) => {
        set({ isLoading: true, error: null });
        let newProjectId = null;

        try {
          const { token, user } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          if (!user || !user.id)
            throw new Error("Builder ID missing for sending invite!");

          let customerId = null;

          // ✅ Step 1: If enquiry exists, reuse customer_id
          const enquiry = get().enquiries.find(
            (enq) => enq.customer_ducktail_id === customerDucktailId
          );
          if (enquiry) {
            customerId = enquiry.customer_id;
            console.log("✅ Existing enquiry found:", customerId);
          }

          // ✅ Step 2: If not found, try fetching customer by Ducktail ID
          if (!customerId) {
            try {
              const findRes = await axiosInstancev1.get(
                `/customers/by-ducktail/${customerDucktailId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (findRes.data.success && findRes.data.customer) {
                customerId = findRes.data.customer.id;
                console.log("✅ Found customer by Ducktail ID:", customerId);
              }
            } catch (findErr) {
              console.warn(
                "⚠️ Customer not found by Ducktail ID, creating new one..."
              );
            }

            // ✅ Step 3: Create new customer if still missing
            if (!customerId) {
              try {
                const createRes = await axiosInstancev1.post(
                  `/customers`,
                  { ducktailId: customerDucktailId, source: "manual_invite" },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (
                  createRes.data.success &&
                  createRes.data.customerId
                ) {
                  customerId = createRes.data.customerId;
                  console.log("✅ New customer created:", customerId);
                }
              } catch (createErr) {
                console.error("❌ Failed to create customer:", createErr);
                toast.error(
                  "Failed to create or fetch customer. Please verify Ducktail ID."
                );
                set({ isLoading: false });
                return {
                  success: false,
                  message: "Failed to create or fetch customer.",
                };
              }
            }
          }

          // ❌ If customerId still missing, stop
          if (!customerId) {
            toast.error("Customer ID missing. Cannot send invite.");
            set({ isLoading: false });
            return { success: false, message: "Customer ID missing." };
          }

          // ✅ Step 4: Proceed to create project
          const projectCreationPayload = {
            builderId: user.id,
            customerId,
            status: "pending_approval",
            ...projectData,
            projectBudget: parseFloat(
              projectData.projectBudget.replace(/,/g, "") || "0"
            ),
          };

          console.log("Final Project Payload:", projectCreationPayload);

          const projectResponse = await axiosInstancev1.post(
            "/projects",
            projectCreationPayload,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (
            projectResponse &&
            (projectResponse.status === 200 ||
              projectResponse.status === 201) &&
            projectResponse.data.success === true &&
            projectResponse.data.projectId
          ) {
            newProjectId = projectResponse.data.projectId;
            console.log(
              `✅ Project created successfully with ID: ${newProjectId}`
            );

            // ✅ Step 5: Add initial budget entry
            const valueEntryPayload = {
              date:
                formatDateForAPI(projectData.startingDate) ||
                formatDateForAPI(new Date().toISOString()),
              comment: "Initial Project Budget/Value",
              status: "Approved",
              amount: parseFloat(
                projectData.projectBudget.replace(/,/g, "") || "0"
              ),
            };

            try {
              await axiosInstancev1.post(
                `/projects/${newProjectId}/value-entries`,
                valueEntryPayload,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              console.log(
                `Initial value entry created for project ${newProjectId}`
              );
            } catch (valueEntryError) {
              console.warn(
                "⚠️ Failed to create value entry:",
                valueEntryError
              );
              toast.warn(
                "Project invite sent, but failed to create initial budget entry."
              );
            }

            toast.success("Project Invite Sent Successfully!");
            return {
              success: true,
              message: "Project Invite Sent Successfully!",
            };
          } else {
            const errorMsg =
              projectResponse.data?.message ||
              "Failed to create project for invite.";
            console.error("Project creation failed:", projectResponse.data);
            throw new Error(errorMsg);
          }
        } catch (error) {
          console.error("🚨 Project Invite Main Catch Error:", error);
          const errorMsg =
            error.message ||
            error.response?.data?.message ||
            "Unexpected error during project invite.";
          set({ error: errorMsg });
          toast.error(errorMsg);
          return { success: false, message: errorMsg };
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "project-enquiries-storage",
      getStorage: () => localStorage,
      partialize: (state) => {
        const { modalComments, isCommentsLoading, isAddingComment, ...rest } =
          state;
        return rest;
      },
    }
  )
);

export default useProjectEnqStore;
